import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Chip, Radio } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { BranchChoice } from '../../useBranchStep';

type BranchCardProps = {
    branch: BranchChoice;
};

/**
 * One branch as a card holding a radio of the step's `RadioGroup`: the whole
 * card body is the radio's label, so a press anywhere selects it and the
 * arrow keys move between branches. Name, address and a "Main" chip when
 * the API flags it. `mt-0` drops the group's top margin on each radio: the
 * card already spaces it, and the margin pushed the content off centre.
 */
export const BranchCard: React.FC<BranchCardProps> = ({ branch }) => {
    const { t } = useTranslation('pages');

    return (
        <Card
            className="p-0"
            {...getNodeTestIdProp(`onboarding-branch-card-${branch.id}`)}
        >
            <Radio className="mt-0 w-full" value={branch.id}>
                <Radio.Content className="w-full items-start gap-3 p-4">
                    <Radio.Control className="mt-0.5">
                        <Radio.Indicator />
                    </Radio.Control>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{branch.name}</span>
                            {branch.isMain ? (
                                <Chip size="sm" variant="soft">
                                    {t('onboarding.branch.main')}
                                </Chip>
                            ) : null}
                        </span>
                        {branch.address ? (
                            <span className="text-sm text-muted">
                                {branch.address}
                            </span>
                        ) : null}
                    </span>
                </Radio.Content>
            </Radio>
        </Card>
    );
};
